/**
 * Authentication Context
 *
 * Manages authentication state, token storage, and user session.
 * Includes activity-based token refresh that maintains a 30-minute
 * session window that resets with user activity.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

import { API_BASE_URL } from '../utils/apiConfig';

// Token refresh configuration
const TOKEN_REFRESH_INTERVAL_MS = 5 * 60 * 1000; // Check every 5 minutes
const ACTIVITY_DEBOUNCE_MS = 30 * 1000; // Debounce activity detection to 30 seconds
const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiry

interface User {
  id: string;
  email: string;
  name: string;
  role?: string;
  roles?: string[];
  accountId: string;
  mfaEnabled?: boolean;
}

interface Account {
  id: string;
  name: string;
  slug: string;
  plan: string;
}

interface AuthState {
  user: User | null;
  account: Account | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterCredentials {
  name: string;
  email: string;
  password: string;
  accountName?: string;
}

interface AuthContextValue extends AuthState {
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; error?: string; requiresMFA?: boolean }>;
  register: (credentials: RegisterCredentials) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshAuth: () => Promise<boolean>;
  verifyMFA: (email: string, mfaToken: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = 'cm_token';
const REFRESH_TOKEN_KEY = 'cm_refresh_token';
const USER_KEY = 'cm_user';
const LAST_ACTIVITY_KEY = 'cm_last_activity';

/**
 * Decode JWT to get expiration time (without verification)
 */
function getTokenExpiry(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : null; // Convert to milliseconds
  } catch {
    return null;
  }
}

/**
 * Check if token is expired or will expire soon
 */
function isTokenExpiringSoon(token: string, bufferMs: number = TOKEN_EXPIRY_BUFFER_MS): boolean {
  const expiry = getTokenExpiry(token);
  if (!expiry) return true; // If we can't determine, assume it needs refresh
  return Date.now() + bufferMs >= expiry;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    account: null,
    token: null,
    refreshToken: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Refs for activity tracking and refresh interval
  const lastActivityRef = useRef<number>(Date.now());
  const refreshIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Internal refresh function
  const refreshAuthInternal = useCallback(async (refreshToken: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        const { accessToken, refreshToken: newRefreshToken, user } = data.data;

        localStorage.setItem(TOKEN_KEY, accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
        localStorage.setItem(USER_KEY, JSON.stringify(user));
        localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());

        setState(prev => ({
          ...prev,
          user,
          token: accessToken,
          refreshToken: newRefreshToken,
          isAuthenticated: true,
          isLoading: false,
        }));

        console.log('[Auth] Token refreshed successfully');
        return true;
      }
    } catch (error) {
      console.error('[Auth] Error refreshing token:', error);
    }

    return false;
  }, []);

  // Activity handler - debounced to prevent excessive calls
  const handleActivity = useCallback(() => {
    // Clear existing timeout
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }

    // Debounce activity updates
    activityTimeoutRef.current = setTimeout(() => {
      lastActivityRef.current = Date.now();
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    }, ACTIVITY_DEBOUNCE_MS);
  }, []);

  // Periodic token refresh check
  const checkAndRefreshToken = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (!token || !refreshToken) {
      return;
    }

    // Check if token is expiring soon
    if (isTokenExpiringSoon(token)) {
      console.log('[Auth] Token expiring soon, attempting refresh...');
      const success = await refreshAuthInternal(refreshToken);
      if (!success) {
        console.log('[Auth] Refresh failed, user may need to re-login');
      }
    }
  }, [refreshAuthInternal]);

  // Setup activity listeners and refresh interval
  useEffect(() => {
    // Activity events to track
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    // Add activity listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Setup periodic refresh check
    refreshIntervalRef.current = setInterval(checkAndRefreshToken, TOKEN_REFRESH_INTERVAL_MS);

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });

      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }

      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
    };
  }, [handleActivity, checkAndRefreshToken]);

  // Load stored auth on mount — render immediately from cache, verify in background
  useEffect(() => {
    const loadStoredAuth = async () => {
      try {
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        const storedUser = localStorage.getItem(USER_KEY);

        if (storedToken && storedUser) {
          const parsedUser = JSON.parse(storedUser);

          // If token isn't expired locally, render the app immediately with cached data
          // and verify with the server in the background
          if (!isTokenExpiringSoon(storedToken, 0)) {
            // Render immediately — no blocking network call
            setState({
              user: parsedUser,
              account: null,
              token: storedToken,
              refreshToken: storedRefreshToken,
              isAuthenticated: true,
              isLoading: false,
            });

            // Background verify — update account data and catch revoked tokens
            fetch(`${API_BASE_URL}/api/auth/verify`, {
              headers: { 'Authorization': `Bearer ${storedToken}` },
            })
              .then(async (response) => {
                if (response.ok) {
                  const data = await response.json();
                  setState(prev => ({
                    ...prev,
                    user: data.data.user,
                    account: data.data.account,
                  }));
                } else {
                  // Server rejected token — try refresh or clear session
                  let recovered = false;
                  if (storedRefreshToken) {
                    recovered = await refreshAuthInternal(storedRefreshToken);
                  }
                  if (!recovered) {
                    localStorage.removeItem(TOKEN_KEY);
                    localStorage.removeItem(REFRESH_TOKEN_KEY);
                    localStorage.removeItem(USER_KEY);
                    localStorage.removeItem(LAST_ACTIVITY_KEY);
                    setState({
                      user: null, account: null, token: null,
                      refreshToken: null, isAuthenticated: false, isLoading: false,
                    });
                  }
                }
              })
              .catch(() => {
                // Network error during background verify — keep cached session,
                // the periodic refresh will retry later
              });
            return;
          }

          // Token already expired — must refresh before rendering
          if (storedRefreshToken) {
            console.log('[Auth] Token expired, attempting refresh...');
            const refreshed = await refreshAuthInternal(storedRefreshToken);
            if (refreshed) return;
          }
        }
      } catch (error) {
        console.error('[Auth] Error loading stored auth:', error);
      }

      // No valid cached session — show login
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(LAST_ACTIVITY_KEY);

      setState({
        user: null,
        account: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
      });
    };

    loadStoredAuth();
  }, [refreshAuthInternal]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.message || 'Login failed' };
      }

      // Check if MFA is required
      if (data.requiresMFA) {
        return { success: false, requiresMFA: true };
      }

      const { accessToken, refreshToken, token, user } = data.data;
      const finalToken = accessToken || token;

      localStorage.setItem(TOKEN_KEY, finalToken);
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      }
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());

      setState({
        user,
        account: null,
        token: finalToken,
        refreshToken: refreshToken || null,
        isAuthenticated: true,
        isLoading: false,
      });

      return { success: true };
    } catch (error) {
      console.error('[Auth] Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }, []);

  const register = useCallback(async (credentials: RegisterCredentials) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.message || 'Registration failed' };
      }

      const { token, refreshToken, user, account } = data.data;

      localStorage.setItem(TOKEN_KEY, token);
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      }
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());

      setState({
        user,
        account,
        token,
        refreshToken: refreshToken || null,
        isAuthenticated: true,
        isLoading: false,
      });

      return { success: true };
    } catch (error) {
      console.error('[Auth] Registration error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }, []);

  const verifyMFA = useCallback(async (email: string, mfaToken: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/mfa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, mfaToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.message || 'MFA verification failed' };
      }

      const { accessToken, refreshToken, user } = data.data;

      localStorage.setItem(TOKEN_KEY, accessToken);
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      }
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());

      setState({
        user,
        account: null,
        token: accessToken,
        refreshToken: refreshToken || null,
        isAuthenticated: true,
        isLoading: false,
      });

      return { success: true };
    } catch (error) {
      console.error('[Auth] MFA verification error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }, []);

  const logout = useCallback(() => {
    // Clear refresh interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);

    setState({
      user: null,
      account: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  const refreshAuth = useCallback(async () => {
    const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (storedRefreshToken) {
      return refreshAuthInternal(storedRefreshToken);
    }
    return false;
  }, [refreshAuthInternal]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        register,
        logout,
        refreshAuth,
        verifyMFA,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
